import React, { useState, useEffect, useRef, memo } from "react";
import { v4 as uuidv4 } from 'uuid';
import axios from "axios";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Typography,
  Switch,
  FormControlLabel,
  TextField
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useApi } from "../../../services/useApi";
import { ApiSuccessResponse } from "../../../types/api";
import DataTable from "../../../components/common/DataTable";
import MappingCell from "../components/MappingCell";
import MappingDialogs from "../components/MappingDialogs";
import { deepClean } from "../../../utils/objectUtils";
import { useCommonOptions } from '../../../hooks/useCommonOptions';
import SubmissionReportDialog, { SubmissionReport, BatchError } from '../components/SubmissionReportDialog';
import { useDynamicMappings } from '../../../hooks/useDynamicMappings';
import ValueCell from "../components/ValueCell";
import OneSnackbar from "../components/OneSnackbar";
import SubmitOptions from "../components/SubmitOptions";
import {UpdateIfExistsMap, BatchRecordError, BatchResponse,Pagination, MappingSubmitResponse} from '../../../interfaces/CommonInterface';
import { dineroFinanceColumns } from "../../../constants/DineroContants";
import { defaultPageSize, rowPerPageOptions } from "../../../constants/CommonConstants";
import { scrollToErrorRow, validateMapping } from "../../../hooks/useCommonValidate";
import UploadData from "../components/UploadData";
import DataSourceTable from "../components/DataSourceTable";
import { useAutoMapping } from "../../../hooks/useAutoMapping";
import { authService } from "../../../services/auth.service";


type SupplierPaymentType = {
  "-1": "None",
  "1": "Plus71",
  "2": "Plus73",
  "5": "Plus04",
  "7": "BankTransfer",
  "10": "Iban",
  "14": "EntryOnly",
  "17": "Plus75"
}

interface EconomicEntry {
  index?: number;
  amount: string;
  currency: string;
  currencyOriginal?: string;
  date: string;
  entryTypeNumber: string;
  entryTypeNumberOriginal?: string;
  journalNumber: string;
  voucherNumber: string;
  accountNumber?: string;
  contraAccountNumber?: string;
  contraVatCode?: string;
  costTypeNumber?: string;
  customerInvoiceNumber?: string;
  customerNumber?: string;
  dueDate?: string;
  projectNumber?: string;
  supplierInvoiceNumber?: string;
  supplierNumber?: string;
  supplierPaymentBank?: string;
  supplierPaymentType?: keyof SupplierPaymentType;
  text?: string;

  vatCode?: string;
  vatCodeOriginal?: string;

  invoiceNumber?: string;
  contactNumber?: string;
}

interface FinancePanelProps {}


interface Mapping{
  contactGuid?: string;
  amount: string;
  currency: string;
  date: string;
  entryTypeNumber: string;
  entryTypeNumberOriginal?: string;
  journalNumber: string;
  voucherNumber: string;
  accountNumber?: string;
  contraAccountNumber?: string;
  contraVatCode?: string;
  costTypeNumber?: string;
  customerInvoiceNumber?: string;
  customerNumber?: string;
  dueDate?: string;
  projectNumber?: string;
  supplierInvoiceNumber?: string;
  supplierNumber?: string;
  supplierPaymentBank?: string;
  supplierPaymentType?: keyof SupplierPaymentType;
  text?: string;

  vatCode?: string;
  vatCodeOriginal?: string;

  invoiceNumber?: string;
  contactNumber?: string;
}

interface DefaultValues extends Mapping {}


const FinancePanel: React.FC<FinancePanelProps> = () => {
  const [sourceData, setSourceData] = useState<any[][]>([]);
  const [originalSourceData, setOriginalSourceData] = useState<any[][]>([]);
  const [sourceColumns, setSourceColumns] = useState<
    { id: string; label: string }[]
  >([]);
  const [skipLines, setSkipLines] = useState<number>(0);

  const [economicEntries, setEconomicEntries] = useState<EconomicEntry[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | string[] | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Mapping>({
    amount: "",
    currency: "",
    date: "",
    entryTypeNumber: "",
    journalNumber: "",
    voucherNumber: "",
    accountNumber: "",
    contactNumber: "",
    contraAccountNumber: "",
    contraVatCode: "",
    costTypeNumber: "",
    customerInvoiceNumber: "",
    customerNumber: "",
    dueDate: "",
    projectNumber: "",
    invoiceNumber: "",
    supplierNumber: "",
    supplierPaymentBank: "",
    text: "",
    vatCode: ""
  });
  const [batchSize, setBatchSize] = useState(10);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [submissionId, setSubmissionId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(0);
  const [shouldStop, setShouldStop] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [sourceDataHeaderRow, setSourceDataHeaderRow] = useState<any[]>([]);

  const [defaultValues, setDefaultValues] = useState<DefaultValues>({
    amount: "",
    currency: "",
    date: "",
    entryTypeNumber: "",
    journalNumber: "",
    voucherNumber: "",
    accountNumber: "",
    contactNumber: "",
    contraAccountNumber: "",
    contraVatCode: "",
    costTypeNumber: "",
    customerInvoiceNumber: "",
    customerNumber: "",
    dueDate: "",
    projectNumber: "",
    invoiceNumber: "",
    supplierNumber: "",
    supplierPaymentBank: "",
    text: "",
    vatCode: ""
  });
  const [requiredFields] = useState<Array<keyof Mapping>>([
    'amount',
    'currency',
    'date',
    'entryTypeNumber',
    'journalNumber',
    'voucherNumber'
  ]);
  const [showReport, setShowReport] = useState(false);
  const [submissionReport, setSubmissionReport] = useState<SubmissionReport | null>(null);
  const [errorRecords, setErrorRecords] = useState<Set<string>>(new Set());
  const tableRef = useRef<HTMLDivElement>(null);
  const [updateIfExistsMap, setUpdateIfExistsMap] = useState<UpdateIfExistsMap>({});

  const api = useApi();
  const { journals,entryTypeOptions, isLoading: isLoadingOptions,currencies,vatCodes } = useCommonOptions();
  const { getMappedValue: getDynamicMappedValue} = useDynamicMappings();

  const [sequenceInfo, setSequenceInfo] = useState<{
    prefix: string;
    startNumber: number;
    numberLength: number;
  } | null>(null);
  const [currentField, setCurrentField] = useState<string | null>(null);

  const columnMappings = {

    journalNumber: [
      "journal number",
      "journal no",
      "journal",
      "journal id",
      "no.",
      "JournalNr",
    ],
    entryTypeNumber: [
      "entry type",
      "type",
      "entry type number",
      "entry type no",
      "BilagsType",
    ],
    date: [
      "date",
      "transaction date",
      "entry date",
      "Dato",
    ],
    contactNumber: [
      "contact",
      "contact number",
      "contact no",
      "customer",
      "supplier",
      "KontaktNr",
      'Konto'
    ],
    accountNumber: [
      "account",
      "account number",
      "account no",
      "KontoNr",
    ],
    voucherNumber: [
      "voucher",
      "voucher number",
      "voucher no",
      "BilagsNr",
      'Bilag'
    ],
    text: [
      "text",
      "description",
      "note",
      "Tekst",
    ],
    amount: [
      "amount",
      "sum",
      "total",
      "Beløb",
    ],
    currency: [
      "currency",
      "curr",
      "Valuta",
    ],
    vatCode: [
      "vat code",
      "vat",
      "tax code",
      "MomsKode",

    ],
    contraAccountNumber: [
      "contra account",
      "contra account number",
      "contra account no",
      "ModkontoNr",
    ],
    contraVatCode: [
      "contra vat code",
      "contra vat",
      "contra tax code",
      "ModkontoMomsKode",
    ],
    invoiceNumber: [
      "invoice",
      "invoice number",
      "invoice no",
      "FakturaNr",
      'Faktura'
    ],
  };

  const { handleAutoMapFirstRow, autoMappingDone, setAutoMappingDone } = useAutoMapping<Mapping>({
    sourceData,
    originalSourceData,
    mapping,
    setMapping,
    setSourceColumns,
    setSuccess,
    setSkipLines,
    onSourceDataUpdate: (newData) => {
      setSourceData(newData);
      setTotalCount(newData.length);
    },
    columnMappings,
  });

  const handleSequenceChange = (field: string, value: string) => {
    const match = value.match(/^([A-Za-z]*)(\d+)$/);
    if (match) {
      const [, prefix, numberStr] = match;
      const numberLength = numberStr.length;
      const startNumber = parseInt(numberStr, 10);
      if (!isNaN(startNumber)) {
        setCurrentField(field);
        setSequenceInfo({
          prefix,
          startNumber: startNumber,
          numberLength
        });
      }
    }

    // const match = sequence.prefix.match(/^([A-Za-z]*)(\d+)$/);

    // if (match) {
    //   const [, prefix, number] = match;
    //   const numberLength = number.length;
    //   setCurrentField(field);
    //   setSequenceInfo({
    //     prefix,
    //     startNumber: parseInt(number),
    //     numberLength
    //   });
    //   // Immediately update the mapping when sequence changes
    //   // mapDineroToEconomic();
    // }
  };
  // Add useEffect to remap data when skipLines changes
  useEffect(() => {
    if (sourceData.length > 0) {
      mapSourceToEconomic(sourceData);
    }
  }, [skipLines, sourceData]);

  // Add useEffect to handle mapping updates
  useEffect(() => {
    if (autoMappingDone && sourceData.length > 0) {
      mapSourceToEconomic(sourceData);
      setAutoMappingDone(false);
    }
  }, [mapping, autoMappingDone, sourceData]);

  // Calculate dynamic batch size based on total records
  useEffect(() => {
    if (economicEntries.length > 0) {
      let newBatchSize = 10; // Default minimum

      if (economicEntries.length <= 100) {
        newBatchSize = 10;
      } else if (economicEntries.length <= 500) {
        newBatchSize = 50;
      } else if (economicEntries.length <= 1000) {
        newBatchSize = 100;
      } else {
        newBatchSize = 500; // Maximum batch size for very large datasets
      }

      setBatchSize(newBatchSize);
    }
  }, [economicEntries.length]);

  const handleSkipLinesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.max(0, parseInt(e.target.value) || 0);
    setSkipLines(value);
  };
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const getDefaultValue = (field: keyof DefaultValues) => {
    return defaultValues[field];
  };

  const mapSourceToEconomic = (sourceData: any[][]) => {
    // console.log('sourceDataHeaderRow',sourceDataHeaderRow);
    // console.log('sourceData',sourceData);
    const dataToProcess = sourceData.slice(skipLines);
    const mappedEntries = dataToProcess.map((item, index) => {
      function getMappedValue(field: string): {
        mapped: string | null;
        original: string | null;
      } {
        const def = getDefaultValue(field as keyof DefaultValues);
        if (def) return { mapped: def, original: null };

        if (field in mapping) {
          // console.log('mapping',mapping);
          const key = mapping[field];
          if (key) {
            // Convert key to column index (assuming key is in format "col0", "col1", etc.)
            const colIndex = parseInt(key.replace("col", ""));
            if (!isNaN(colIndex) && colIndex < item.length) {
              const raw = item[colIndex];
              const mapped = getDynamicMappedValue(field, raw, item,mapping,sourceDataHeaderRow);
              return { mapped, original: raw };
            }
          }
        }
        return { mapped: null, original: null };
      }
      const { mapped: entryTypeNumber, original: entryTypeNumberOriginal } = getMappedValue("entryTypeNumber");
      const { mapped: vatCode, original: vatCodeOriginal } = getMappedValue("vatCode");
      const { mapped: currency, original: currencyOriginal } = getMappedValue("currency");
      return {
        index: index + skipLines,
        amount:getMappedValue("amount").mapped,
        currency:currency,
        currencyOriginal:currencyOriginal,
        date:getMappedValue("date").mapped,
        entryTypeNumber:entryTypeNumber,
        entryTypeNumberOriginal:entryTypeNumberOriginal,
        journalNumber:getMappedValue("journalNumber").mapped,
        voucherNumber:getMappedValue("voucherNumber").mapped,
        contactNumber:getMappedValue("contactNumber").mapped,
        accountNumber:getMappedValue("accountNumber").mapped,
        contraAccountNumber:getMappedValue("contraAccountNumber").mapped,
        contraVatCode:getMappedValue("contraVatCode").mapped,
        costTypeNumber:getMappedValue("costTypeNumber").mapped,
        // customerInvoiceNumber:getMappedValue("customerInvoiceNumber").mapped,
        customerNumber:getMappedValue("customerNumber").mapped,
        dueDate:getMappedValue("dueDate").mapped,
        projectNumber:getMappedValue("projectNumber").mapped,
        invoiceNumber:getMappedValue("invoiceNumber").mapped,
        supplierNumber:getMappedValue("supplierNumber").mapped,
        supplierPaymentBank:getMappedValue("supplierPaymentBank").mapped,
        text:getMappedValue("text").mapped,
        vatCode:vatCode,
        vatCodeOriginal:vatCodeOriginal
      };
    });
    setEconomicEntries(mappedEntries);
    setSuccess("Successfully mapped " + economicEntries.length + " entries");
  };

  const handleUpdateIfExistsChange = (field: string, value: boolean) => {
    setUpdateIfExistsMap(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitMapping = async () => {
    // Validate required fields
    const validationErrors = validateMapping(mapping, defaultValues, requiredFields);
    if (validationErrors.length > 0) {
      setError(validationErrors);
      return;
    }

    // if (updateExistingProduct && !updateKey) {
    //   setError("Please select a key for updating existing products");
    //   return;
    // }

    // Generate unique submission ID for caching
    const newSubmissionId = uuidv4();
    setSubmissionId(newSubmissionId);

    setIsSubmitting(true);
    setSubmitProgress(0);
    setError(null);
    setSuccess(null);
    setErrorRecords(new Set()); // Reset error records
    setShouldStop(false); // Reset stop flag
    setProcessedCount(0); // Reset processed count

    // Create new AbortController for this submission
    abortControllerRef.current = new AbortController();

    const totalRecords = economicEntries.length;
    const batches = Math.ceil(totalRecords / batchSize);
    setTotalBatches(batches);

    let successCount = 0;
    let failedCount = 0;
    let allErrors: (string | BatchRecordError)[] = [];

    try {
      for (let i = 0; i < batches; i++) {
        console.log(`Processing batch ${i + 1} of ${batches}`);
        
        // Check if should stop
        if (shouldStop) {
          console.log('Process stopped by user');
          break;
        }

        const start = i * batchSize;
        const end = Math.min(start + batchSize, totalRecords);
        const batchItems = economicEntries.slice(start, end);

        const filteredBatch = batchItems
          .map(item => deepClean(item))
          .filter(item => item && Object.keys(item).length > 0);

        // Create axios instance with longer timeout for batch processing
        const submitApi = axios.create({
          baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
          timeout: 600000, // 10 minutes timeout for batch processing
          headers: {
            'Content-Type': 'application/json',
          },
        });

        // Add auth token
        const token = authService.getToken();
        if (token) {
          submitApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }

        // Retry mechanism for batch processing
        let response;
        let retryCount = 0;
        const maxRetries = 3;
        
        while (retryCount < maxRetries) {
          // Check if should stop before each attempt
          if (shouldStop || (abortControllerRef.current && abortControllerRef.current.signal.aborted)) {
            console.log(`Batch ${i + 1} cancelled due to stop request`);
            throw new Error('cancelled');
          }
          
          try {
            response = await submitApi.post<MappingSubmitResponse>(
              "/main/create-entry",
              {
                submissionId: newSubmissionId, // Add submission ID for caching
                mapping,
                entries: filteredBatch,
                batchInfo: {
                  currentBatch: i + 1,
                  totalBatches: batches,
                },
                updateIfExists: updateIfExistsMap,
                isLastBatch: i === batches - 1 // Add flag to indicate last batch
              },
              {
                signal: abortControllerRef.current?.signal
              }
            );
            break; // Success, exit retry loop
          } catch (error: any) {
            // Check if it's a cancellation error
            if (error.name === 'AbortError' || error.message === 'canceled' || error.message === 'cancelled') {
              console.log(`Batch ${i + 1} was cancelled`);
              throw new Error('cancelled');
            }
            
            retryCount++;
            console.log(`Batch ${i + 1} attempt ${retryCount} failed:`, error.message);
            
            if (retryCount >= maxRetries) {
              throw error; // Re-throw if max retries reached
            }
            
            // Wait before retry (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
          }
        }

        const batchResponse = response.data.data;
        successCount += batchResponse.successCount;
        failedCount += batchResponse.failedCount;
        setProcessedCount(successCount); // Update processed count

        if (batchResponse.errors && batchResponse.errors.length > 0) {
          allErrors = [...allErrors, ...batchResponse.errors];
          batchResponse.errors.forEach(error => {
            setErrorRecords(prev => new Set(prev).add(error.recordId));
          });
        }

        setCurrentBatch(i + 1);
        setSubmitProgress(Math.round(((i + 1) / batches) * 100));
        console.log(`Batch ${i + 1} completed successfully`);
        
        // Add small delay between batches to prevent overwhelming the server
        if (i < batches - 1) {
          console.log(`Waiting 500ms before next batch...`);
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Only process errors and show report if not stopped
      if (!shouldStop) {
        const reportErrors: (string | BatchError)[] = [];
        allErrors.forEach(error => {
          if (typeof error === 'string') {
            reportErrors.push(error);
          } else {
            error.errors.forEach(err => {
              reportErrors.push({
                recordId: error.recordId,
                field: '',
                message: err,
                value: ''
              });
            });
          }
        });

        setSubmissionReport({
          successCount,
          failedCount,
          errors: reportErrors
        });
        setShowReport(true);

        if (reportErrors.length > 0) {
          setError(
            `Failed to process ${failedCount} records. Click "View Report" for details.`
          );
        } else {
          const successMessage = `Successfully processed ${successCount} records.`;
          setSuccess(successMessage);
        }
      }
    } catch (error: any) {
      console.error("Error submitting mapping:", error);

      if (error.message === 'cancelled' || error.message === 'canceled' || error.name === 'AbortError') {
        // Handle cancellation
        setShowReport(true);
        setShouldStop(false);
        // Don't show error message for cancellation as it's handled in handleStopSubmit
      } else {
        setError("Failed to submit mapping. Please try again later.");
      }
    } finally {
      setIsSubmitting(false);
      setSubmitProgress(0);
      abortControllerRef.current = null;
      console.log('finally');
    }
  };

  const handleStopSubmit = () => {
    setShouldStop(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Show stop message immediately
    setSuccess(`Process stopped. Successfully processed ${processedCount} records.`);
    setIsSubmitting(false);
    setSubmitProgress(0);
    abortControllerRef.current = null;
  };



  return (
    <>
      <UploadData
        onLoading={(loading) => {
          setLoading(loading);
        }}
        onLoadData={(data, columns) => {
          setOriginalSourceData(data);
          setSourceData(data);
          setSourceColumns(columns);
          setTotalCount(data.length);
          mapSourceToEconomic(data.slice(skipLines));
          if(data.length > 0){
            setSourceDataHeaderRow(data[0]);
          }
        }}
        onError={(msg) => {
          setError(msg);
        }}
      />
      <MappingDialogs
          mapping={mapping}
          defaultValues={defaultValues}
          type="finance"
          onApplyMapping={(newMap, defaults) => {
            setMapping(newMap);
            setDefaultValues(defaults);
          }}
          // availableColumns={dineroColumns.map(col => ({ value: col.id, label: col.label }))}
          onSuccess={(msg) => setSuccess(msg)}
      />


          <SubmitOptions
        mapSourceDataToEconomic={mapSourceToEconomic}
        sourceData={sourceData}
        economicData={economicEntries}
        isSubmitting={isSubmitting}
        handleSubmitMapping={handleSubmitMapping}
        handleStopSubmit={handleStopSubmit}
        submissionReport={submissionReport}
        currentBatch={currentBatch}
        totalBatches={totalBatches}
        submitProgress={submitProgress}
        setShowReport={setShowReport}
        skipLines={skipLines}
        handleSkipLinesChange={handleSkipLinesChange}
        onAutoMapFirstRow={handleAutoMapFirstRow}
        submissionId={submissionId}
      />

    <DataSourceTable
        sourceColumns={sourceColumns}
        sourceData={sourceData}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        skipLines={skipLines}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />


      <Typography
        variant="h5"
        sx={{ mb: 2, fontWeight: "bold", color: "primary.main", mt:2 }}
      >
          E-conomic Entries
      </Typography>

      <TableContainer component={Paper} sx={{ maxHeight: 600 }} ref={tableRef}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "auto",
            "& th, & td": {
              whiteSpace: "nowrap",
            },
          }}
          aria-label="economic customer table"
        >
          <TableHead>
            <TableRow>
            <MappingCell
                key="journalNumber"
                label="Cashbook Number"
                field="journalNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                itemOptions={journals}

                />
            <MappingCell
                key="Type"
                label="Entry Type"
                field="entryTypeNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                itemOptions={entryTypeOptions}
              />
              <MappingCell
                key="date"
                label="Date"
                field="date"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                key="contactNumber"
                label="Customer/Supplier"
                field="contactNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                key="accountNumber"
                label="Account Number"
                field="accountNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                key="voucherNumber"
                label="Voucher Number"
                field="voucherNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                onUpdateIfExistsChange={(value) => handleUpdateIfExistsChange('voucherNumber', value)}
              />
              <MappingCell
                key="text"
                label="Text"
                field="text"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                key="amount"
                label="Amount"
                field="amount"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                key="currency"
                label="Currency"
                field="currency"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                itemOptions={currencies}
              />
              <MappingCell
                key="vatCode"
                label="VAT Code"
                field="vatCode"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                itemOptions={vatCodes}
              />
              <MappingCell
                key="contraAccountNumber"
                label="Contra Account Number"
                field="contraAccountNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                key="contraVatCode"
                label="Contra VAT Code"
                field="contraVatCode"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                itemOptions={vatCodes}
              />
              <MappingCell
                key="invoiceNumber"
                label="Invoice Number"
                field="invoiceNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ padding: "8px" }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : economicEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ padding: "8px" }}>
                  <Typography variant="body2">No data available</Typography>
                </TableCell>
              </TableRow>
            ) : (
              economicEntries
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((entry) => {
                  const hasError = errorRecords.has(
                    entry?.index?.toString() || ""
                  );
                  return (
                    <TableRow
                      key={entry.index}
                      id={`row-${entry.index}`}
                      sx={{
                        backgroundColor: hasError ? 'rgba(255, 0, 0, 0.1)' : undefined,
                        '&:hover': {
                          backgroundColor: hasError ? 'rgba(255, 0, 0, 0.2)' : undefined
                        }
                      }}
                    >
                      <ValueCell value={entry.journalNumber} options={journals}/>
                      <ValueCell value={entry.entryTypeNumber} options={entryTypeOptions} originalValue={entry.entryTypeNumberOriginal}/>
                      <ValueCell value={entry.date} />
                      <ValueCell value={entry.contactNumber} />
                      <ValueCell value={entry.accountNumber} />
                      <ValueCell value={entry.voucherNumber} />
                      <ValueCell value={entry.text} />
                      <ValueCell value={entry.amount} />
                      <ValueCell value={entry.currency} options={currencies} originalValue={entry.currencyOriginal} />
                      <ValueCell value={entry.vatCode} options={vatCodes} originalValue={entry.vatCodeOriginal} />
                      <ValueCell value={entry.contraAccountNumber} />
                      <ValueCell value={entry.contraVatCode} options={vatCodes} />
                      <ValueCell value={entry.invoiceNumber} />
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={rowPerPageOptions}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            position: "sticky",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "#f5f5f5",
            borderTop: "1px solid rgba(224, 224, 224, 1)",
            zIndex: 1,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "0.75rem",
              },
          }}
        />
      </TableContainer>

      <OneSnackbar
        error={error}
        success={success}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
      />

      <SubmissionReportDialog
        open={showReport}
        onClose={() => setShowReport(false)}
        report={submissionReport}
        onErrorClick={(recordId) => {
          scrollToErrorRow(recordId, economicEntries, rowsPerPage, page, setPage, tableRef);
        }}
      />
    </>
  );
};

export default memo(FinancePanel);
